import {
  computed,
  inject,
  Injectable,
  resource,
  ResourceRef,
  Signal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  CONFIGURATION_SETTING_KEYS,
  findMatchingTariffVia,
  IOnDemandRelationProductGroupQuery,
  ITariffRelationVia,
  resolveFareRelevantJourneyPart,
  TariffProductGroup,
} from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { OnDemandRelationProductGroupGateway } from './on-demand-relation-product-group.gateway';
import { OnDemandAvailabilityStore } from './on-demand-availability-store.service';
import { ConfigurationStore } from '../configuration/configuration-store.service';
import { ReconRouteStore } from '../journey-planning/recon/recon-route-store.service';
import { ViasGateway } from '../relations/vias/vias.gateway';

@Injectable({ providedIn: 'root' })
export class OnDemandRelationProductGroupStore {
  private readonly gateway: OnDemandRelationProductGroupGateway = inject(
    OnDemandRelationProductGroupGateway
  );
  private readonly viasGateway: ViasGateway = new ViasGateway();
  private readonly availabilityStore: OnDemandAvailabilityStore = inject(
    OnDemandAvailabilityStore
  );
  private readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  private readonly serviceAreaId = computed(() =>
    this.configurationStore.getNumberConfiguration(
      CONFIGURATION_SETTING_KEYS.ServiceAreaId
    )
  );

  private readonly reconRouteStore: ReconRouteStore = inject(ReconRouteStore);

  private readonly queryParams: Signal<
    IOnDemandRelationProductGroupQuery | undefined
  > = computed(() => {
    const trip = this.reconRouteStore.reconRoute();
    const onDemandLeg = this.availabilityStore.onDemandLeg();
    const serviceAreaId = this.serviceAreaId();

    if (!trip || !onDemandLeg || !serviceAreaId) {
      return undefined;
    }
    if (trip.tariffSegments?.length === 0) {
      return undefined;
    }

    const tariffSegment = trip.tariffSegments?.[0];
    const fareRelevantJourneyPart = resolveFareRelevantJourneyPart(
      trip,
      onDemandLeg,
      tariffSegment
    );

    if (!fareRelevantJourneyPart) {
      return undefined;
    }

    const { departureLeg, origin, destination } = fareRelevantJourneyPart;
    const date = departureLeg.origin?.date;
    const time = departureLeg.origin?.time;

    if (!origin || !destination || !date || !time) {
      return undefined;
    }

    return {
      serviceAreaId,
      origin,
      destination,
      dateTime: `${date}T${time}`,
      viaDescription: tariffSegment?.viaDescription ?? '',
    };
  });

  readonly #resource: ResourceRef<
    IApiResponse<TariffProductGroup[]> | undefined
  > = resource({
    params: this.queryParams,
    loader: async ({ params }) => {
      if (!params) {
        return undefined;
      }

      const { viaDescription, ...relationQuery } = params;
      if (!viaDescription) {
        return firstValueFrom(
          this.gateway.getRelationProductGroups(relationQuery)
        );
      }

      const viaResponse = await firstValueFrom(
        this.viasGateway.get({
          serviceAreaId: relationQuery.serviceAreaId,
          origin: relationQuery.origin,
          destination: relationQuery.destination,
        })
      );
      const matchingVia: ITariffRelationVia | undefined = findMatchingTariffVia(
        viaResponse.data ?? [],
        viaDescription
      );

      return firstValueFrom(
        this.gateway.getRelationProductGroups({
          ...relationQuery,
          via: matchingVia?.via,
        })
      );
    },
  });

  readonly isLoading: Signal<boolean> = computed(() =>
    this.#resource.isLoading()
  );

  readonly productGroups: Signal<TariffProductGroup[]> = computed(
    () => this.#resource.value()?.data ?? []
  );

  readonly firstProductGroup: Signal<TariffProductGroup | undefined> = computed(
    () => this.productGroups()[0]
  );
}
